import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';

export type PoigneeFichier = {
  /** Le fichier retenu, ou rien. */
  fichier: () => File | null;
  /** Remet le champ à blanc après un envoi réussi. */
  vider: () => void;
};

/**
 * Le choix d'un fichier, dit en français.
 *
 * Le champ natif affiche « Choisir un fichier » suivi de « Aucun fichier
 * sélectionné », dans une police que rien ne permet d'accorder au reste, et
 * dont l'apparence change d'un navigateur à l'autre. Surtout, il ne dit pas ce
 * qu'on attend : un justificatif ? une photo ? Le vrai champ reste donc là,
 * masqué, et un bouton ordinaire le déclenche — avec le mot juste.
 *
 * Une fois le fichier choisi, son nom s'affiche : c'est la seule confirmation
 * qu'attend quelqu'un qui vient d'ouvrir un explorateur de fichiers.
 */
const ChoixFichier = forwardRef<PoigneeFichier, {
  libelle?: string;
  accept?: string;
  onChoisi?: (fichier: File | null) => void;
}>(function ChoixFichier({ libelle = 'Parcourir…', accept, onChoisi }, ref) {
  const champ = useRef<HTMLInputElement>(null);
  const [nom, setNom] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    fichier: () => champ.current?.files?.[0] ?? null,
    vider: () => {
      if (champ.current) champ.current.value = '';
      setNom(null);
    },
  }));

  const retirer = () => {
    if (champ.current) champ.current.value = '';
    setNom(null);
    onChoisi?.(null);
  };

  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <input
        ref={champ}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const fichier = e.target.files?.[0] ?? null;
          setNom(fichier?.name ?? null);
          onChoisi?.(fichier);
        }}
      />

      <button
        type="button"
        onClick={() => champ.current?.click()}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap"
      >
        <Paperclip className="w-4 h-4 shrink-0" />
        {nom ? 'Changer de fichier' : libelle}
      </button>

      {nom && (
        <span className="inline-flex items-center gap-1 min-w-0 max-w-[14rem] text-sm text-gray-600 dark:text-gray-300">
          <span className="truncate" title={nom}>
            {nom}
          </span>
          <button
            type="button"
            onClick={retirer}
            title="Retirer ce fichier"
            className="shrink-0 text-gray-400 hover:text-red-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      )}
    </span>
  );
});

export default ChoixFichier;
